// Common
import { Injectable, OnDestroy, inject } from '@angular/core';
import { isNil } from '@modules/common/utils/base';
import { uniqBy } from 'lodash-es';

// Rx
import { BehaviorSubject, EMPTY, Observable, Subject, combineLatest } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  finalize,
  map,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

// Types
import { Section } from '@modules/common/types/section';
import { NestedDataItem } from '@modules/drag-n-drop/types/nested-item';
import { Column } from '../types/column';
import { Project } from '../types/project';
import { Task } from '../types/task';

// Services
import { LSBoolean, LocalStorageItem } from '@frontendlib/common/decorators/local-storage.decorator';
import { SignalsService } from '@frontendlib/common/services/signals.service';
import { AlertService } from '@modules/alert/services/alert.service';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { type StitchRequestConfig } from '@modules/common/services/base-stitch.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { PALETTE_COLORS } from '@modules/form-controls/types/palette-colors';
import { TasksService } from '@modules/tasks/services/tasks.service';
import { DeferInstanceDecorator, applyInstanceDecorators } from '@sharedlib/common/lang/DeferInstanceDecorator';
import { STITCH_LIFECYCLE_EVENTS, SignalStitchEventType, SignalType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { ColumnsService } from './columns.service';
import { SectionsService } from './sections.service';

/**
 * The card menu's personal marks. They write with `emit: false` (no list refresh), so the board reloads on
 * their local signal for a card this board holds and the card's menu offers Unflag / Resnooze / Refollow-up
 * at once. Pin is not here: the task service already refreshes on PINNED/UNPINNED, and the board reloads on
 * that refresh.
 */
const BOARD_MARK_EVENTS: readonly SignalStitchEventType[] = [
  SignalStitchEventType.FLAGGED,
  SignalStitchEventType.UNFLAGGED,
  SignalStitchEventType.SNOOZED,
  SignalStitchEventType.UNSNOOZED,
  SignalStitchEventType.FOLLOWED,
  SignalStitchEventType.UNFOLLOWED,
];

@Injectable()
export class BoardService implements OnDestroy {
  private readonly spaceContextStore = inject(SpaceContextStore);
  private originalSections: Section[] = [];
  private sections = new BehaviorSubject<Section[]>([]);
  private tempSectionCounter = 0;
  private pendingSectionCreates = new Map<string, Observable<Section>>();
  private originalColumns: Column[] = [];
  private columns = new BehaviorSubject<Column[]>([]);
  private originalTasks: Task[] = [];
  private tasks = new BehaviorSubject<Task[]>([]);
  private project: Project;
  private readonly projectOwner = new BehaviorSubject<string | undefined>(undefined);
  /** A board read is in flight: from the reload's start until its cards are installed. */
  private reading = false;
  /** Marked task ids that arrived while a read was in flight and the board did not hold them yet (#2039). */
  private readonly marksDuringRead = new Set<string>();
  private alive = new Subject<void>();
  private reload = new Subject<void>();
  private columnAddFocus = new Subject<string | null>();
  public columnAddFocus$ = this.columnAddFocus.asObservable();
  /**
   * The row (section id, `null` for the project's own row) whose new column takes the cursor. The new column's
   * component is created after {@link addColumn} emits, so it claims this on init instead of hearing the event.
   */
  private pendingColumnFocus: string | null | undefined;
  /** The saved id of a section just added from "Add new section", whose name field takes the cursor. */
  private pendingSectionFocus: string | undefined;

  @DeferInstanceDecorator(LSBoolean({ lsKey: 'board-section.collapsed' }))
  declare public readonly sectionCollapsed: LocalStorageItem<boolean>;
  @DeferInstanceDecorator(LSBoolean({ lsKey: 'board-column.collapsed' }))
  declare public readonly columnCollapsed: LocalStorageItem<boolean>;

  constructor(
    private columnsService: ColumnsService,
    private sectionsService: SectionsService,
    private tasksService: TasksService,
    private signalsService: SignalsService,
    private alertService: AlertService,
  ) {
    applyInstanceDecorators(this, BoardService);
    this.sections.pipe(takeUntil(this.alive)).subscribe((sections) => {
      if (sections.some(({ id }) => this.isTempSection(id))) {
        return;
      }

      this.syncOrder(sections, this.originalSections, this.sectionsService);
      this.originalSections = sections;
    });

    this.columns.pipe(takeUntil(this.alive)).subscribe((columns) => {
      if (columns.some(({ id }) => id === 'temp')) {
        return;
      }

      this.syncOrder(columns, this.originalColumns, this.columnsService);
      this.originalColumns = columns;
    });

    this.tasks.pipe(takeUntil(this.alive)).subscribe((tasks) => {
      if (tasks.some(({ id }) => id === 'temp')) {
        return;
      }

      this.syncTasks(tasks);
      this.originalTasks = tasks;
    });

    this.reload
      .pipe(
        filter(() => !!this.project?.id),
        tap(() => (this.reading = true)),
        switchMap(() =>
          combineLatest([
            this.sectionsService.findAll({ containersIds: [this.project.id] }, this.itemRequestConfig()),
            this.columnsService.findAll({ projectId: this.project.id }, this.itemRequestConfig()),
          ]),
        ),
        tap(([sections, columns]) => {
          this.originalSections = sections;
          this.originalColumns = columns;

          // Keep unsaved drafts alive across reloads triggered by saving a sibling
          // section, so adding several sections before naming them isn't undone.
          const pendingSections = this.sections.getValue().filter((section) => this.isTempSection(section.id));

          this.sections.next(
            [...sections, ...pendingSections]
              .sort((a, b) => a.position - b.position)
              .map((section, index) => new Section({ ...section, position: index })),
          );

          // Keep this project's unsaved (still Untitled) column where it was added; pending first, so the stable
          // sort keeps it left of the saved column whose position it took.
          const pendingColumns = this.columns
            .getValue()
            .filter(({ id, projectId }) => id === 'temp' && projectId === this.project.id);

          this.columns.next(
            [...pendingColumns, ...columns]
              .sort((a, b) => a.position - b.position)
              .map((column, index) => new Column({ ...column, position: index })),
          );
        }),
        switchMap(() => this.tasksService.findAll({ containersIds: [this.project.id] }, this.itemRequestConfig())),
        takeUntil(this.alive),
      )
      .subscribe((tasks) => {
        const sectionIds = this.originalSections.map(({ id }) => id);
        const columnsIds = this.originalColumns.map(({ id }) => id);
        const uncategorizedColumnId = this.originalColumns.find(({ uncategorized }) => uncategorized).id;

        this.originalTasks = tasks
          .filter((task) => !task.deleted && !task.archived)
          .map(
            (task) =>
              new Task({
                ...task,
                columnId:
                  task.columnId === null || !columnsIds.includes(task.columnId) ? uncategorizedColumnId : task.columnId,
                sectionId: sectionIds.includes(task.sectionId) ? task.sectionId : null,
              }),
          );

        this.tasks.next(this.originalTasks);
        this.reading = false;

        // A mark made while this read was loading reached a board that did not hold the card yet, and this read
        // may have left the server before the mark was written: read once more if one of those cards is here.
        const marked = [...this.marksDuringRead];
        this.marksDuringRead.clear();
        if (this.holdsAnyTask(marked)) {
          this.reload.next();
        }
      });

    this.projectOwner
      .pipe(
        switchMap((owner) =>
          this.signalsService
            .getSignal(SignalType.RELOAD_PROJECT_BOARD, owner ?? this.spaceContextStore.contextId())
            .pipe(
              filter(({ containerId }) => containerId === this.project?.id),
              debounceTime(300),
            ),
        ),
        takeUntil(this.alive),
      )
      .subscribe(() => this.reload.next());

    this.projectOwner
      .pipe(
        switchMap((owner) =>
          this.signalsService.getSignal(SignalType.STITCH_EVENT, owner ?? this.spaceContextStore.contextId()).pipe(
            filter(
              (event) =>
                event.stitchType === StitchType.TASK &&
                (STITCH_LIFECYCLE_EVENTS.includes(event.eventType) ||
                  (BOARD_MARK_EVENTS.includes(event.eventType) && this.holdsMarkedTask(event.ids))),
            ),
            debounceTime(300),
          ),
        ),
        takeUntil(this.alive),
      )
      .subscribe(() => this.reload.next());

    // A write through the tasks service (a card menu's Pin, Flag, Snooze, Follow up, Mark complete, the
    // card's circle) refreshes the lists; the board's cards are a one-shot read, so it reloads too.
    this.tasksService
      .getRefresh()
      .pipe(debounceTime(300), takeUntil(this.alive))
      .subscribe(() => this.reload.next());
  }

  setProject(project: Project) {
    // Marks held for the board being left are not this board's: nothing is replayed on the new one.
    this.marksDuringRead.clear();
    this.project = project;
    this.projectOwner.next(project?.accountId);
    this.reload.next();
  }

  /** Returns request configuration pinned to the open project's tenant. */
  public itemRequestConfig(): StitchRequestConfig {
    return { requestContext: owningTenantContext(this.project?.accountId) };
  }

  getSections() {
    return this.sections.asObservable();
  }

  getColumns() {
    return this.columns.asObservable();
  }

  getTasks(column: Column, section: Section) {
    return this.tasks.pipe(
      map((items) =>
        // Subtasks are managed from the parent's detail view, not surfaced on the board.
        items.filter(
          (task) => task.parentId === null && task.sectionId === (section?.id || null) && task.columnId == column.id,
        ),
      ),
    );
  }

  getTasksCount(column: Column) {
    return this.tasks.pipe(
      map((items) => items.filter((task) => task.parentId === null && task.columnId == column.id).length),
    );
  }

  /**
   * Whether a mark on these task ids redraws a card this board holds now. While a read is in flight, ids the board does
   * not hold yet are kept for that read's end ({@link marksDuringRead}) instead of being dropped.
   */
  private holdsMarkedTask(ids: readonly string[] | undefined): boolean {
    if (this.holdsAnyTask(ids)) {
      return true;
    }
    if (this.reading) {
      for (const id of ids ?? []) this.marksDuringRead.add(id);
    }
    return false;
  }

  /** Whether one of these task ids is a card this board holds; no ids hold nothing. */
  private holdsAnyTask(ids: readonly string[] | undefined): boolean {
    if (!ids?.length) {
      return false;
    }

    const held = new Set(this.tasks.getValue().map(({ id }) => id));

    return ids.some((id) => held.has(id));
  }

  private isTempSection(id?: string): boolean {
    return !!id && id.startsWith('temp-');
  }

  private createTempSectionId(): string {
    return `temp-${++this.tempSectionCounter}`;
  }

  private replaceSection(id: string, section: Section): void {
    this.sections.next(
      uniqBy(
        this.sections.getValue().map((item) => (item.id === id ? section : item)),
        'id',
      ),
    );
  }

  private renumberSections(sections: Section[]): Section[] {
    return sections
      .sort((a, b) => a.position - b.position)
      .map((section, index) => new Section({ ...section, position: index }));
  }

  private rollbackDraft(tempId: string): void {
    const sections = this.sections.getValue();
    if (!sections.some(({ id }) => id === tempId)) {
      return;
    }

    this.sections.next(this.renumberSections(sections.filter(({ id }) => id !== tempId)));
  }

  addSection(after = true, position?: number): void {
    const sections = this.sections.getValue();

    const finalPosition = isNil(position) ? sections.length - 1 : position;

    const tempId = this.createTempSectionId();

    const newSections = [
      new Section({
        id: tempId,
        containerId: this.project.id,
        position: after ? finalPosition + 0.5 : finalPosition - 0.5,
      }),
      ...sections,
    ]
      .sort((a, b) => a.position - b.position)
      .map(
        (section, index) =>
          new Section({
            ...section,
            position: index,
            title: this.isTempSection(section.id) && !section.title ? `Section ${index + 2}` : section.title,
          }),
      );

    this.sections.next(newSections);

    // Persist on add so the section exists on the backend even if its default title
    // is never edited; the optimistic draft is then swapped for the saved section.
    const draft = newSections.find((section) => section.id === tempId);
    const created$ = this.sectionsService.create(draft, this.itemRequestConfig()).pipe(
      // On failure drop the optimistic draft so it can't linger and block reordering.
      catchError(() => {
        this.rollbackDraft(tempId);
        return EMPTY;
      }),
      finalize(() => this.pendingSectionCreates.delete(tempId)),
      shareReplay(1),
      takeUntil(this.alive),
    );
    this.pendingSectionCreates.set(tempId, created$);

    created$.subscribe((created) => {
      this.pendingSectionFocus = created.id;
      this.replaceSection(tempId, created);
    });
  }

  /** True once for the section just added, so its row puts the cursor in its name (Johanna, 2026-10-21). */
  claimSectionFocus(sectionId: string | undefined): boolean {
    if (!sectionId || this.pendingSectionFocus !== sectionId) return false;
    this.pendingSectionFocus = undefined;
    return true;
  }

  /** True once for the new column of the row that asked for it (see {@link pendingColumnFocus}). */
  claimColumnFocus(sectionId: string | null): boolean {
    if (this.pendingColumnFocus === undefined || this.pendingColumnFocus !== sectionId) return false;
    this.pendingColumnFocus = undefined;
    return true;
  }

  addColumn(after = true, position?: number, originatingSectionId: string | null = null) {
    const columns = this.columns.getValue().filter((column) => column.id !== 'temp');

    const finalPosition = isNil(position) ? columns.length - 1 : position;

    const newColumns = [
      new Column({
        id: 'temp',
        projectId: this.project.id,
        position: after ? finalPosition + 0.5 : finalPosition - 0.5,
      }),
      ...columns,
    ]
      .sort((a, b) => a.position - b.position)
      .map(
        (column, index) =>
          new Column({
            ...column,
            position: index,
            color: column.id === 'temp' ? PALETTE_COLORS[index % PALETTE_COLORS.length] : column.color,
          }),
      );

    this.pendingColumnFocus = originatingSectionId;
    this.columns.next(newColumns);
    this.columnAddFocus.next(originatingSectionId);
  }

  addTask(task: Task) {
    this.tasks.next([task, ...this.tasks.getValue()]);
  }

  updateSection(section: Section): void {
    if (this.isTempSection(section.id) && (section.title ?? '').trim() === '') {
      return;
    }

    this.sections.next(this.sections.getValue().map((i) => (i.id === section.id ? section : i)));

    const pendingCreate = this.pendingSectionCreates.get(section.id);

    if (pendingCreate) {
      // Renamed before its create round-trip finished — apply the rename once the
      // backend id is known, so we update the section instead of creating a second.
      pendingCreate
        .pipe(
          switchMap((created) =>
            this.sectionsService.update(new Section({ ...section, id: created.id }), this.itemRequestConfig()),
          ),
          take(1),
          takeUntil(this.alive),
        )
        .subscribe((saved) => this.replaceSection(saved.id, saved));

      return;
    }

    // Fallback for a draft whose create never completed; otherwise a normal update.
    const request = this.isTempSection(section.id)
      ? this.sectionsService.create(section, this.itemRequestConfig())
      : this.sectionsService.update(section, this.itemRequestConfig());

    request.pipe(take(1), takeUntil(this.alive)).subscribe((saved) => this.replaceSection(section.id, saved));
  }

  updateColumn(column: Column) {
    if (column.id === 'temp' && !column.title?.trim()) {
      return;
    }

    this.columns.next(this.columns.getValue().map((i) => (i.id === column.id ? column : i)));

    // The project this save belongs to. Its answer may land after the reused project window opened another
    // project, whose own Untitled draft is also 'temp' (#2043): then it changes nothing on screen — that board's
    // next read brings the saved column — and otherwise it replaces only this project's draft or column.
    const projectId = this.project?.id;
    this.columnsService
      .upsert(column, this.itemRequestConfig())
      .pipe(takeUntil(this.alive))
      .subscribe((upsertedColumn) => {
        if (this.project?.id !== projectId) {
          return;
        }
        this.columns.next(
          uniqBy(
            this.columns
              .getValue()
              .map((i) => (i.id === column.id && (i.projectId ?? projectId) === projectId ? upsertedColumn : i)),
            'id',
          ),
        );
      });
  }

  public moveSection(section: Section, direction: 1 | -1) {
    const sections = this.sections.getValue();

    const newSections = [...sections]
      .map((i) => new Section({ ...i, position: i.id === section.id ? i.position + direction * 1.5 : i.position }))
      .sort((a, b) => a.position - b.position)
      .map((i, index) => new Section({ ...i, position: index }));

    this.sections.next(newSections);
  }

  public moveColumn(column: Column, direction: 1 | -1) {
    const columns = this.columns.getValue();

    const newColumns = [...columns]
      .map((i) => new Column({ ...i, position: i.id === column.id ? i.position + direction * 1.5 : i.position }))
      .sort((a, b) => a.position - b.position)
      .map((i, index) => new Column({ ...i, position: index }));

    this.columns.next(newColumns);
  }

  public removeSection(section: Section) {
    const pendingCreate = this.pendingSectionCreates.get(section.id);

    if (pendingCreate) {
      // Draft is still being created — delete the real section once its id is known,
      // otherwise a delete with the client-only id no-ops and leaves a backend orphan.
      pendingCreate
        .pipe(
          switchMap((created) => this.sectionsService.deletePermanently([created.id], true, this.itemRequestConfig())),
          take(1),
          takeUntil(this.alive),
        )
        .subscribe();
    } else if (!this.isTempSection(section.id)) {
      this.sectionsService
        .deletePermanently([section.id], true, this.itemRequestConfig())
        .pipe(take(1), takeUntil(this.alive))
        .subscribe();
    }

    this.sections.next(this.renumberSections(this.sections.getValue().filter(({ id }) => id !== section.id)));
  }

  public removeColumn(column: Column) {
    this.alertService.show({
      title: 'Delete Column',
      body: 'Deleting this column will move all tasks in it to the trash. Are you sure?',
      rightButtons: [
        { title: 'CANCEL', close: true },
        {
          title: 'DELETE',
          close: true,
          click: () => this.deleteColumn(column),
        },
      ],
    });
  }

  private deleteColumn(column: Column): void {
    this.columnsService
      .deletePermanently([column.id], true, this.itemRequestConfig())
      .pipe(take(1), takeUntil(this.alive))
      .subscribe();

    const newColumns = this.columns
      .getValue()
      .filter(({ id }) => id !== column.id)
      .sort((a, b) => a.position - b.position)
      .map((column, index) => new Column({ ...column, position: index }));

    this.columns.next(newColumns);
  }

  setColumnCollapsed(columnId: string, value: boolean): void {
    this.columnCollapsed.set(value, columnId);
  }

  getColumnCollapsed(key: string): Observable<boolean> {
    return this.columnCollapsed.get(key);
  }

  setSectionCollapsed(sectionId: string, value: boolean): void {
    this.sectionCollapsed.set(value, sectionId);
  }

  getSectionCollapsed(key: string): Observable<boolean> {
    return this.sectionCollapsed.get(key);
  }

  ngOnDestroy() {
    this.detach();
  }

  detach() {
    this.alive.next();
    this.alive.complete();
  }

  private syncOrder(
    items: (Section | Column)[],
    originalItems: (Section | Column)[],
    service: SectionsService | ColumnsService,
  ) {
    const itemsMap = new Map<string, Section | Column>();

    for (const item of originalItems) {
      itemsMap.set(item.id, item);
    }

    const changedItems = items.filter((item) => {
      const mapItem = itemsMap.get(item.id);
      return mapItem && item.position !== mapItem.position;
    });

    if (!changedItems.length) {
      return;
    }

    service
      .reorder(
        changedItems.map(({ id, position }) => ({ id, position })),
        this.itemRequestConfig(),
      )
      .pipe(take(1), takeUntil(this.alive))
      .subscribe();
  }

  private syncTasks(items: Task[]) {
    const itemsMap = new Map<string, Task>();

    for (const item of this.originalTasks) {
      itemsMap.set(item.id, item);
    }

    const changedItems = items.filter((item) => {
      const mapItem = itemsMap.get(item.id);
      return (
        !mapItem ||
        item.boardPosition !== mapItem.boardPosition ||
        item.projectId !== mapItem.projectId ||
        item.sectionId !== mapItem.sectionId ||
        item.columnId !== mapItem.columnId ||
        item.parentId !== mapItem.parentId
      );
    });

    if (!changedItems.length) {
      return;
    }

    this.tasksService
      .reorderBoard(changedItems, this.itemRequestConfig())
      .pipe(take(1), takeUntil(this.alive))
      .subscribe();
  }

  updateTasks(changes: NestedDataItem<Task>[], sectionId: string, columnId: string) {
    const changesIds = changes.map(({ id }) => id);

    const tasks = this.tasks.getValue();

    const newTasks = changes.map(
      (i) =>
        new Task({
          ...i.data,
          boardPosition: i.position,
          parentId: i.data.parentId,
          sectionId: sectionId || null,
          columnId,
          projectId: this.project.id,
        }),
    );

    this.tasks.next([...tasks.filter(({ id }) => !changesIds.includes(id)), ...newTasks]);
  }

  /**
   * List view (sorted mode): move tasks into a column (and its section) without
   * manual reordering — they are reassigned to `columnId`/`sectionId` and appended
   * to that group's board order, then the active sort decides where they display.
   */
  public moveTasksToColumn(items: { id?: string }[], sectionId: string | undefined, columnId: string): void {
    if (!this.project) {
      return;
    }

    const ids = new Set(items.map(({ id }) => id));
    const all = this.tasks.getValue();

    let nextPosition =
      all
        .filter((task) => task.parentId === null && task.sectionId === (sectionId || null) && task.columnId == columnId)
        .reduce((max, task) => Math.max(max, task.boardPosition ?? 0), -1) + 1;

    const updated = all.map((task) =>
      ids.has(task.id)
        ? new Task({
            ...task,
            sectionId: sectionId || null,
            columnId,
            parentId: null,
            boardPosition: nextPosition++,
            projectId: this.project.id,
          })
        : task,
    );

    this.tasks.next(updated);
  }
}
